import { formatDistanceToNow } from "date-fns";
import { QrCode, Clock, Image, Table, Trash2 } from "lucide-react";
import { Order } from "@/types/orders";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { OrderStatusBadge } from "@/components/orders/OrderStatusBadge";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { formatCurrency } from "@/lib/currencyFormatter";
import { useOrderStore } from "@/store/orderStore";
import { useState } from "react";
import { ConfirmDeleteDialog } from "@/components/tables/ConfirmDeleteDialog";
import { useIsMobile } from "@/hooks/use-mobile";
import { PaymentMethodBadge } from "./PaymentMethodBadge";
import { OrderHeader } from "./OrderHeader";

interface OrderHistoryItemProps {
  order: Order;
  onClick: () => void;
}

export function OrderHistoryItem({ order, onClick }: OrderHistoryItemProps) {
  const { t } = useTranslation();
  const { deleteOrder } = useOrderStore();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const isMobile = useIsMobile();

  // Calculate total items quantity
  const totalQuantity = order.items.reduce(
    (sum, item) => sum + item.quantity,
    0
  );

  // Handle delete order
  const handleDeleteOrder = async () => {
    try {
      await deleteOrder(order.id);
      setShowDeleteDialog(false);
    } catch (error) {
      console.error("Error deleting order:", error);
    }
  };

  // Stop propagation to prevent opening order details when clicking delete
  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowDeleteDialog(true);
  };

  // Find an image URL from any of the items, or use placeholder
  const foodImageUrl =
    order.items.find((item) => item.imageUrl)?.imageUrl ||
    "https://placehold.co/300x200/e2e8f0/64748b?text=Food+Image";

  return (
    <>
      <Card
        className="overflow-hidden transition-all duration-200 hover:shadow-md cursor-pointer"
        onClick={onClick}
      >
        {/* Order Header with ID, Status and Actions */}
        {/* <CardHeader className="p-3 sm:p-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b">
          <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
            <div className="flex items-center gap-2">
              <div className="bg-primary/10 p-1.5 sm:p-2 rounded-full">
                <QrCode className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
              </div>
              <div>
                <h3 className="font-bold text-sm sm:text-base">
                  {t("order")} #{order.id.slice(0, 8)}
                </h3>
                <div className="flex items-center text-xs sm:text-sm text-muted-foreground">
                  <Clock className="h-3 w-3 mr-1" />
                  {formatDistanceToNow(new Date(order.createdAt), {
                    addSuffix: true,
                  })}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2 justify-between sm:justify-normal">
              <div className="text-right">
                <div className="font-bold text-base sm:text-lg">
                  {formatCurrency(order.totalAmount)}
                </div>
                <div className="flex gap-2">
                  <Badge variant="outline" className="bg-gray-100 text-xs">
                    {totalQuantity}{" "}
                    {totalQuantity === 1 ? t("item") : t("items")}
                  </Badge>
                  {order.payment_method && (
                    <PaymentMethodBadge method={order.payment_method} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </CardHeader> */}

        <OrderHeader order={order} showPaymentStatus={false} />

        <CardContent className="p-0">
          {/* Table Info and Status Section */}
          <div className="p-3 sm:p-4 bg-gray-50 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
            <div className="flex items-center">
              <Table className="h-4 w-4 mr-2 text-gray-500" />
              <span className="font-medium text-sm sm:text-base">
                {t("table")} {order.tableNumber} - {order.tableName}
              </span>
            </div>

            <div className="flex flex-row items-center gap-2 mt-1 sm:mt-0">
              <OrderStatusBadge status={order.status} />

              {/* Delete Button */}
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-destructive hover:text-destructive hover:bg-destructive/10"
                onClick={handleDeleteClick}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </div>

          {/* Order Items Section - Simplified for history view */}
          <div className="p-3 sm:p-4">
            <div className="space-y-3">
              {order.items.slice(0, 2).map((item) => (
                <div key={item.id} className="flex items-center gap-2 sm:gap-3">
                  {/* Item Image */}
                  <div className="h-12 w-12 sm:h-16 sm:w-16 rounded overflow-hidden bg-gray-100 flex-shrink-0">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.menu_item_name}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center text-gray-400">
                        <Image className="h-4 w-4 sm:h-5 sm:w-5" />
                      </div>
                    )}
                  </div>

                  {/* Item Details */}
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <h4 className="font-medium text-sm sm:text-base truncate mr-2">
                        {item.menu_item_name}
                      </h4>
                      <span className="font-medium text-sm sm:text-base whitespace-nowrap">
                        {formatCurrency(item.price * item.quantity)}
                      </span>
                    </div>
                    <div className="flex items-center text-xs sm:text-sm text-muted-foreground mt-0.5">
                      <span>{item.quantity}x</span>
                      <span className="mx-2">•</span>
                      <span>{formatCurrency(item.price)}</span>
                    </div>
                  </div>
                </div>
              ))}

              {/* Show "more items" indicator if there are more than 2 items */}
              {order.items.length > 2 && (
                <div className="text-sm text-muted-foreground pt-1 text-center">
                  + {order.items.length - 2} {t("moreItems")}
                </div>
              )}
            </div>
          </div>

          {/* Footer Section with Order Info */}
          <div className="p-3 sm:p-4 bg-gray-50 border-t">
            <div className="flex items-center justify-between text-xs sm:text-sm">
              <div className="text-muted-foreground">
                {new Date(order.createdAt).toLocaleTimeString([], {
                  hour: "2-digit",
                  minute: "2-digit",
                })}{" "}
                • {new Date(order.createdAt).toLocaleDateString()}
              </div>
              <Badge variant="outline" className="text-xs">
                ID: {order.id.slice(-6)}
              </Badge>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog - Moved outside the Card component */}
      <ConfirmDeleteDialog
        open={showDeleteDialog}
        onOpenChange={setShowDeleteDialog}
        onConfirm={handleDeleteOrder}
        itemName={`Order #${order.id.slice(0, 8)}`}
        title={t("deleteOrder")}
        description={t("deleteOrderConfirmation")}
      />
    </>
  );
}
